'use client';

import {
  makeAutoObservable,
  runInAction,
} from 'mobx';

import type { AssistantStore } from './assistantStore';
import type { AssistantMcpApproval } from './assistantTypes';
import type {
  GatewayMcpApprovalDecision,
  GatewayMcpApprovalDefaultMode,
  GatewayMcpApprovalRequest,
  GatewayMcpApprovalSettings,
  GatewayMcpApprovalToolMode,
} from '@/app/sdk';

const DEFAULT_MCP_APPROVAL_SETTINGS: GatewayMcpApprovalSettings = {
  defaultMode: 'auto',
  toolModes: {},
};
const MCP_REQUEST_APPROVAL_TOOL = 'genesys_request_approval';

function resolveMcpToolsFromOperations(operations: string[]): string[] {
  const tools = operations
    .map(operation => operation.split('.', 1)[0]?.trim() ?? '')
    .filter(Boolean);
  const uniqueTools = tools.filter((tool, index, list) => list.indexOf(tool) === index);
  return uniqueTools.length > 0 ? uniqueTools : [MCP_REQUEST_APPROVAL_TOOL];
}

export function normaliseMcpApprovalSettings(settings: GatewayMcpApprovalSettings | undefined): GatewayMcpApprovalSettings {
  if (!settings) {
    return DEFAULT_MCP_APPROVAL_SETTINGS;
  }
  return {
    defaultMode: settings.defaultMode === 'prompt' ? 'prompt' : 'auto',
    toolModes: Object.fromEntries(
      Object.entries(settings.toolModes ?? {}).filter(([, mode]) => isMcpApprovalToolMode(mode)),
    ),
  };
}

function isMcpApprovalToolMode(mode: unknown): mode is GatewayMcpApprovalToolMode {
  return mode === 'auto' || mode === 'prompt' || mode === 'disabled';
}

export class AssistantApprovalStore {
  mcpApproval: GatewayMcpApprovalSettings = DEFAULT_MCP_APPROVAL_SETTINGS;
  mcpApprovals: AssistantMcpApproval[] = [];

  private mcpApprovalExpiryTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(private readonly root: AssistantStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isMcpApprovalAtDefaults(): boolean {
    return this.mcpApproval.defaultMode === 'auto'
      && Object.keys(this.mcpApproval.toolModes).length === 0;
  }

  applyMcpApprovalSettings(settings: GatewayMcpApprovalSettings | undefined): void {
    this.mcpApproval = normaliseMcpApprovalSettings(settings);
  }

  clearApprovals(): void {
    this.mcpApprovals = [];
    this.clearMcpApprovalExpiryTimers();
  }

  disposeMcpApprovalTimers(): void {
    this.clearMcpApprovalExpiryTimers();
  }

  async setMcpApprovalDefaultMode(defaultMode: GatewayMcpApprovalDefaultMode): Promise<void> {
    const nextSettings: GatewayMcpApprovalSettings = {
      ...this.mcpApproval,
      defaultMode,
      toolModes: { ...this.mcpApproval.toolModes },
    };
    runInAction(() => {
      this.mcpApproval = nextSettings;
    });
    await this.root.updateGatewaySettings({ mcpApproval: nextSettings });
  }

  async setMcpApprovalToolMode(toolName: string, mode: GatewayMcpApprovalToolMode): Promise<void> {
    const trimmedToolName = toolName.trim();
    if (!trimmedToolName) {
      return;
    }

    const nextToolModes = {
      ...this.mcpApproval.toolModes,
      [trimmedToolName]: mode,
    };
    const nextSettings: GatewayMcpApprovalSettings = {
      ...this.mcpApproval,
      toolModes: nextToolModes,
    };
    runInAction(() => {
      this.mcpApproval = nextSettings;
    });
    await this.root.updateGatewaySettings({ mcpApproval: nextSettings });
  }

  async resetMcpApprovalSettings(): Promise<void> {
    const nextSettings: GatewayMcpApprovalSettings = {
      defaultMode: 'auto',
      toolModes: {},
    };
    runInAction(() => {
      this.mcpApproval = nextSettings;
    });
    await this.root.updateGatewaySettings({ mcpApproval: nextSettings });
  }

  async autoApproveMcpApproval(approvalId: string): Promise<void> {
    const approval = this.mcpApprovals.find(item => item.approvalId === approvalId);
    if (!approval || approval.status !== 'pending') {
      return;
    }

    const tools = resolveMcpToolsFromOperations(approval.operations);
    const nextToolModes = { ...this.mcpApproval.toolModes };
    for (const tool of tools) {
      nextToolModes[tool] = 'auto';
    }
    const nextSettings: GatewayMcpApprovalSettings = {
      ...this.mcpApproval,
      toolModes: nextToolModes,
    };
    runInAction(() => {
      this.mcpApproval = nextSettings;
    });
    await this.root.updateGatewaySettings({ mcpApproval: nextSettings });
    this.respondToMcpApproval(approvalId, { status: 'approved' });
  }

  respondToMcpApproval(approvalId: string, decision: GatewayMcpApprovalDecision): void {
    const gateway = window.electronAPI?.gateway;
    const approval = this.mcpApprovals.find(item => item.approvalId === approvalId);
    if (!gateway || !approval || approval.status !== 'pending') {
      return;
    }

    this.clearMcpApprovalExpiryTimer(approvalId);
    gateway.respondToMcpApproval(approvalId, decision);
    runInAction(() => {
      this.mcpApprovals = this.mcpApprovals.map(item => (
        item.approvalId === approvalId
          ? {
            ...item,
            status: decision.status === 'approved' ? 'approved' : 'rejected',
            reason: decision.reason,
          }
          : item
      ));
    });
  }

  handleMcpApprovalRequest(event: GatewayMcpApprovalRequest): void {
    this.clearMcpApprovalExpiryTimer(event.approvalId);
    runInAction(() => {
      const approval: AssistantMcpApproval = {
        approvalId: event.approvalId,
        summary: event.summary,
        operations: [...event.operations],
        expiresAt: event.expiresAt,
        status: 'pending',
      };
      const existingIndex = this.mcpApprovals.findIndex(item => item.approvalId === event.approvalId);
      this.mcpApprovals = existingIndex >= 0
        ? this.mcpApprovals.map((item, index) => index === existingIndex ? approval : item)
        : [...this.mcpApprovals, approval];
    });
    this.scheduleMcpApprovalExpiry(event);
  }

  private scheduleMcpApprovalExpiry(event: GatewayMcpApprovalRequest): void {
    const expiresAtMs = Date.parse(event.expiresAt);
    if (!Number.isFinite(expiresAtMs)) {
      return;
    }
    const timeoutMs = Math.max(0, expiresAtMs - Date.now());
    const timer = setTimeout(() => {
      this.mcpApprovalExpiryTimers.delete(event.approvalId);
      const gateway = window.electronAPI?.gateway;
      gateway?.respondToMcpApproval(event.approvalId, {
        status: 'rejected',
        reason: 'Approval request expired.',
      });
      runInAction(() => {
        this.mcpApprovals = this.mcpApprovals.map(item => (
          item.approvalId === event.approvalId && item.status === 'pending'
            ? { ...item, status: 'expired', reason: 'Approval request expired.' }
            : item
        ));
      });
    }, timeoutMs);
    this.mcpApprovalExpiryTimers.set(event.approvalId, timer);
  }

  private clearMcpApprovalExpiryTimer(approvalId: string): void {
    const timer = this.mcpApprovalExpiryTimers.get(approvalId);
    if (!timer) {
      return;
    }
    clearTimeout(timer);
    this.mcpApprovalExpiryTimers.delete(approvalId);
  }

  private clearMcpApprovalExpiryTimers(): void {
    for (const timer of this.mcpApprovalExpiryTimers.values()) {
      clearTimeout(timer);
    }
    this.mcpApprovalExpiryTimers.clear();
  }
}
